import { ExternalLink, Eye, MessageSquare, ThumbsUp } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  useVideoSnapshots,
  useVideoYoutube,
  type VideoComment,
  type VideoSnapshot,
  type VideoYoutube,
} from '@/api/endpoints/analytics'
import { Section } from '@/components/ds/Section'
import { Button } from '@/components/ui/Button'
import { formatRelativeDate, formatScheduledLabel } from '@/lib/format'
import { canChangeVisibility } from '@/features/projects/selection'
import { PRIVACY_LABEL, VisibilityDialog } from '@/features/projects/components/VisibilityDialog'
import styles from './YoutubeDock.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

export interface YoutubeDockProps {
  videoId: string
  lang: string
  /** Título do vídeo, para a lista da troca de visibilidade. */
  title?: string
}

/**
 * The project's "YouTube" tab, in three sections:
 *
 *   1. **Informações gerais** — counters of now (views, likes, comments) from
 *      ``/youtube`` (the hourly stats sync) and the video's real visibility,
 *      changeable here (docs/plan-youtube-visibility.md).
 *   2. **Métricas** — the YouTube Analytics snapshots (``/snapshots``: t48h,
 *      t7d, t30d): engaged views, retention, subs. The first one lands ~5 days
 *      after publication (2-day window + 72h of Analytics lag).
 *   3. **Comentários** — newest first, read live from the Data API.
 *
 * A video not published yet shows each section with its own empty state.
 * Recharts is loaded only here so the rest of the app doesn't pay its bundle
 * cost.
 */
export function YoutubeDock({ videoId, lang, title }: YoutubeDockProps) {
  const youtube = useVideoYoutube({ videoId, lang })
  const snapshots = useVideoSnapshots({ videoId, lang })
  const yt = youtube.data
  const published = Boolean(yt?.youtube_video_id)
  const retry = () => {
    void youtube.refetch()
  }

  let info: ReactNode
  let comments: ReactNode
  if (youtube.isPending) {
    info = <div className={styles.note}>Carregando...</div>
    comments = info
  } else if (youtube.isError) {
    info = <QueryErrorNotice compact error={youtube.error} label="os dados do YouTube" onRetry={retry} />
    comments = <QueryErrorNotice compact error={youtube.error} label="os comentários" onRetry={retry} />
  } else if (!yt || !published) {
    info = <div className={styles.note}>Publique o vídeo para ver views, curtidas e a visibilidade no YouTube.</div>
    comments = <div className={styles.note}>Publique o vídeo para ver os comentários.</div>
  } else {
    info = <GeneralInfo yt={yt} videoId={videoId} lang={lang} title={title ?? videoId} />
    comments = <CommentsBlock yt={yt} />
  }

  const commentCount = yt?.comment_list.length ?? 0
  return (
    <div className={styles.shell}>
      <Section
        title="Informações gerais"
        padding="sm"
        actions={
          yt?.url ? (
            <a className={styles.ytLink} href={yt.url} target="_blank" rel="noopener noreferrer">
              Abrir no YouTube <ExternalLink aria-hidden />
            </a>
          ) : undefined
        }
      >
        {info}
      </Section>
      <Section title="Métricas" padding="sm">
        {published || youtube.isPending ? (
          <SnapshotsBlock
            snapshots={snapshots.data}
            pending={snapshots.isPending}
            error={
              snapshots.isError ? (
                <QueryErrorNotice
                  compact
                  error={snapshots.error}
                  label="os snapshots"
                  onRetry={() => {
                    void snapshots.refetch()
                  }}
                />
              ) : null
            }
          />
        ) : (
          <div className={styles.note}>Publique o vídeo para ver retenção, engaged views e inscritos.</div>
        )}
      </Section>
      <Section
        title={`Comentários${published && commentCount > 0 ? ` · ${String(commentCount)} mais recentes` : ''}`}
        padding="sm"
      >
        {comments}
      </Section>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Informações gerais: counters of now + visibility
// ---------------------------------------------------------------------------

function GeneralInfo({ yt, videoId, lang, title }: { yt: VideoYoutube; videoId: string; lang: string; title: string }) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const synced = yt.synced_at ? `atualizado ${formatRelativeDate(yt.synced_at)}` : 'aguardando sincronizar'
  const scheduledLabel = isFuture(yt.scheduled_for) ? formatScheduledLabel(yt.scheduled_for) : null
  const privacy = yt.youtube_privacy ?? null
  const canChange = canChangeVisibility(yt) && !scheduledLabel
  const privacyMeta = scheduledLabel
    ? 'para mudar, use o Scheduler'
    : yt.youtube_privacy_synced_at
      ? `atualizado ${formatRelativeDate(yt.youtube_privacy_synced_at)}`
      : 'aguardando sincronizar'

  return (
    <div className={styles.kpiRow4}>
      <Kpi icon={<Eye />} label="Views" value={countOrDash(yt.views)} meta={synced} />
      <Kpi icon={<ThumbsUp />} label="Curtidas" value={countOrDash(yt.likes)} meta={rateMeta(yt.likes, yt.views)} />
      <Kpi
        icon={<MessageSquare />}
        label="Comentários"
        value={countOrDash(yt.comments)}
        meta={rateMeta(yt.comments, yt.views)}
      />
      {canChangeVisibility(yt) ? (
        <div className={styles.kpi}>
          <span className={styles.kpiLabel}>Visibilidade</span>
          <span className={styles.kpiValue}>
            {scheduledLabel ?? (privacy ? PRIVACY_LABEL[privacy] : '—')}
          </span>
          <span className={styles.kpiMeta}>{privacyMeta}</span>
          {canChange ? (
            <Button
              variant="ghost"
              size="sm"
              className={styles.kpiAction}
              onClick={() => {
                setDialogOpen(true)
              }}
            >
              Alterar
            </Button>
          ) : null}
          {canChange ? (
            <VisibilityDialog
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              items={[{ videoId, lang, youtubeId: yt.youtube_video_id, title, privacy }]}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function isFuture(iso: string | null | undefined): boolean {
  if (!iso) return false
  const t = new Date(iso).getTime()
  return Number.isFinite(t) && t > Date.now()
}

// ---------------------------------------------------------------------------
// Snapshots (YouTube Analytics at fixed ages)
// ---------------------------------------------------------------------------

function SnapshotsBlock({
  snapshots: data,
  pending,
  error,
}: {
  snapshots: VideoSnapshot[] | undefined
  pending: boolean
  /** A nota de erro já montada (``QueryErrorNotice compact``), ou null. */
  error: ReactNode
}) {
  // The backend returns snapshots ordered by snapshot_age — t48h first,
  // then t7d, then t30d. Memoise so the chart's data doesn't see a fresh
  // ``[]`` reference every render.
  const snapshots = useMemo(() => data ?? [], [data])
  const chartData = useMemo(
    () =>
      snapshots.map((s) => ({
        age: ageLabel(s.snapshot_age),
        views: s.views,
        engaged: s.engaged_views,
      })),
    [snapshots],
  )
  const latest = snapshots[snapshots.length - 1]

  if (pending) return <div className={styles.note}>Carregando snapshots...</div>
  if (error) return error
  if (!latest) {
    return (
      <div className={styles.note}>
        Retenção, engaged views e inscritos vêm do YouTube Analytics, amostrados em 48h, 7d e 30d. O
        primeiro chega uns 5 dias depois da publicação: a janela de 48h mais o atraso de ~3 dias do
        Analytics.
      </div>
    )
  }

  const avp = Math.round(latest.average_view_percentage * 100)
  return (
    <>
      <div className={styles.section}>
        <span className={styles.sectionTitle}>YouTube Analytics · em {ageLabel(latest.snapshot_age)}</span>
        <div className={styles.kpiRow3}>
          <Kpi label="Engaged views" value={formatCompact(latest.engaged_views)} meta="≥30s assistidos" />
          <Kpi label="Retenção" value={`${String(avp)}%`} meta="média / vídeo" />
          <Kpi label="Subs ganhos" value={`+${formatCompact(latest.subscribers_gained)}`} meta="cumulativo" />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.chartCard}>
          <div className={styles.chartHead}>
            <span className={styles.chartTitle}>Views por idade</span>
            <span className={styles.chartLegend}>
              <span className={styles.legendDot} aria-hidden /> views
            </span>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={chartData} margin={{ top: 4, right: 12, bottom: 4, left: 0 }}>
              <defs>
                <linearGradient id="viewsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--brand-ignite)" stopOpacity={0.32} />
                  <stop offset="100%" stopColor="var(--brand-ignite)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="age" stroke="var(--fg-subtle)" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis
                stroke="var(--fg-subtle)"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatCompact}
                width={38}
              />
              <Tooltip
                cursor={{ stroke: 'var(--border-strong)', strokeDasharray: '2 3' }}
                contentStyle={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  fontSize: 12,
                }}
                labelStyle={{ color: 'var(--fg-muted)' }}
                formatter={(value) => formatCompact(typeof value === 'number' ? value : Number(value))}
              />
              <Area
                type="monotone"
                dataKey="views"
                stroke="var(--brand-ignite)"
                strokeWidth={2}
                fill="url(#viewsFill)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className={styles.section}>
        <span className={styles.sectionTitle}>Snapshots brutos</span>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Idade</th>
                <th className={styles.numCell}>Views</th>
                <th className={styles.numCell}>Engaged</th>
                <th className={styles.numCell}>Likes</th>
                <th className={styles.numCell}>Comments</th>
                <th className={styles.numCell}>Subs</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map((s) => (
                <tr key={s.snapshot_age}>
                  <td>{ageLabel(s.snapshot_age)}</td>
                  <td className={styles.numCell}>{formatCompact(s.views)}</td>
                  <td className={styles.numCell}>{formatCompact(s.engaged_views)}</td>
                  <td className={styles.numCell}>{formatCompact(s.likes)}</td>
                  <td className={styles.numCell}>{formatCompact(s.comments)}</td>
                  <td className={styles.numCell}>+{formatCompact(s.subscribers_gained)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

function CommentsBlock({ yt }: { yt: VideoYoutube }) {
  const list = yt.comment_list
  let body: ReactNode
  if (yt.comments_status === 'disabled') {
    body = <div className={styles.note}>Os comentários estão desligados neste vídeo.</div>
  } else if (yt.comments_status === 'unavailable') {
    body = (
      <div className={styles.note}>
        Não foi possível ler os comentários{yt.comments_error ? `: ${yt.comments_error}` : '.'}
      </div>
    )
  } else if (list.length === 0) {
    body = <div className={styles.note}>Nenhum comentário ainda.</div>
  } else {
    body = (
      <ul className={styles.comments}>
        {list.map((c) => (
          <CommentItem key={c.comment_id} comment={c} />
        ))}
      </ul>
    )
  }
  return body
}

function CommentItem({ comment }: { comment: VideoComment }) {
  return (
    <li className={styles.comment}>
      <div className={styles.commentHead}>
        <span className={styles.commentAuthor}>{comment.author || 'Anônimo'}</span>
        <time className={styles.commentDate} dateTime={comment.published_at}>
          {formatRelativeDate(comment.published_at)}
        </time>
        {comment.like_count > 0 ? (
          <span className={styles.commentLikes}>
            <ThumbsUp aria-hidden /> {formatCompact(comment.like_count)}
          </span>
        ) : null}
      </div>
      <p className={styles.commentText}>{comment.text}</p>
    </li>
  )
}

// ---------------------------------------------------------------------------
// KPIs
// ---------------------------------------------------------------------------

function Kpi({ icon, label, value, meta }: { icon?: ReactNode; label: string; value: string; meta: string }) {
  return (
    <div className={styles.kpi}>
      <span className={styles.kpiLabel}>
        {icon ? <span className={styles.kpiIcon} aria-hidden>{icon}</span> : null}
        {label}
      </span>
      <span className={styles.kpiValue}>{value}</span>
      <span className={styles.kpiMeta}>{meta}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function rateMeta(n: number | null | undefined, views: number | null | undefined): string {
  if (n == null || !views) return '—'
  return `${((n / views) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% das views`
}

function countOrDash(n: number | null | undefined): string {
  return n == null ? '—' : formatCompact(n)
}

function ageLabel(snapshotAge: string): string {
  // Backend uses lowercase enum values: t48h, t7d, t30d. Render as
  // human-friendly tokens that fit a chart axis.
  switch (snapshotAge) {
    case 't48h':
      return '48h'
    case 't7d':
      return '7d'
    case 't30d':
      return '30d'
    default:
      return snapshotAge.replace(/^t/, '')
  }
}

function formatCompact(n: number): string {
  if (!Number.isFinite(n)) return '0'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`
  return String(n)
}
